import Swal from "sweetalert2";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";

// Services
import { api } from "@service/api";

// Components
import { Card } from "@components/cards/card";

// Hooks
import {
  getPlatformLogContentLoadedInfo,
  getPlatformLogPingInfo,
  getPlatformLogTypeInfo,
} from "@hooks/functions/PlatformLog";
import { Masks } from "@hooks/functions/Masks/Masks";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { formatName } from "@hooks/functions/formatName";
import { IPlatformListDTO } from "@hooks/dtos/IPlatformDTO";
import {
  getPlatformLastHealthStatusInfo,
  getPlatformStatusInfo,
} from "@hooks/functions/Platform";

// Icons
import { CgGlobe } from "react-icons/cg";
import { FaClock, FaHistory, FaServer, FaUser } from "react-icons/fa";
import { FiActivity, FiFileText, FiZap } from "react-icons/fi";
import { FaList, FaWifi } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { LoadingSkeleton } from "@components/skeleton-loading";

const masks = new Masks();
export function PlatformHealthList() {
  const navigate = useNavigate();
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [platforms, setPlatforms] = useState<IPlatformListDTO[]>([]);
  const [platformIds, setPlatformIds] = useState<number[]>([]);

  const formatResponseTime = (time?: number) => {
    if (!time) return "-";
    return `${masks.formatMilhar(time)} ms`;
  };

  const formatDate = (date?: Date | string) => {
    if (!date) return "-";
    return new Date(date).toLocaleString("pt-BR");
  };

  const getPlatformHealthChecks = async () => {
    try {
      const result = await api.get(`/misc/healthMonitor/list`);
      const _platforms = result?.data?.platforms;
      // console.log({ _platforms });
      setPlatforms(_platforms);
      setIsLoading(false);
    } catch (error: any) {
      console.error(error);
      pushToastify({
        message: error?.message || "Houve um erro ao carregar os Health Checks!",
        type: "error",
        theme: "dark",
        pauseOnHover: true,
      });
    }
  };

  const handlePlatformHealthCheck = async () => {
    try {
      Swal.fire({
        title: "Confirmar execução",
        text: "Tem certeza que deseja executar uma nova análise?",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sim, continuar.",
        cancelButtonText: "Cancelar",
        reverseButtons: true,
      }).then(async (result) => {
        if (result.isConfirmed) {
          // --- Set Loading and Send Info Message --- //
          setIsLoading(true);
          pushToastify({
            message: "Executando monitoramento, por favor aguarde...",
            type: "info",
            theme: "dark",
            pauseOnHover: true,
          });

          const params = `?`;
          const data = {
            platforms: platformIds,
          };
          const result = await api.post(
            `/misc/healthMonitor/check${params}`,
            data
          );

          // --- Load again the Platform Health Check List --- //
          getPlatformHealthChecks();

          // --- Update Message --- //
          pushToastify({
            message: "Monitoramento executado com sucesso.",
            type: "success",
            theme: "dark",
            pauseOnHover: true,
          });
        }
      });
    } catch (error: any) {
      console.error(error);
      pushToastify({
        message: error?.message || "Houve um erro ao executar o Health Check!",
        type: "error",
        theme: "dark",
        pauseOnHover: true,
      });
    }
  };

  useEffect(() => {
    getPlatformHealthChecks();
  }, []);

  useEffect(() => {
    const platIds = platforms.map((p) => p.id);
    setPlatformIds(platIds);
  }, [platforms]);

  const onlineCount = platforms.filter((p) => p.status === "a").length;
  const totalCount = platforms.length;

  return (
    <>
      {/* Main Page Header */}
      <Card className="position-relative">
        <div className="card-body">
          <div className="row align-items-center">
            <div className="col-md-8">
              <div className="d-flex align-items-center mb-3">
                <div className="bg-success bg-opacity-10 rounded p-3 me-3">
                  <FiActivity className="text-white" size={32} />
                </div>
                <div>
                  <h4 className="mb-1">Monitor de Saúde das Plataformas</h4>
                  {!isLoading && (
                    <div className="badge bg-primary fs-6 px-3 py-2">
                      {onlineCount}/{totalCount} Online
                    </div>
                  )}
                </div>
              </div>
              <p className="text-muted mb-0">
                Acompanhe o status e desempenho de todas as plataformas.
              </p>
              <p className="text-muted mb-0">
                Monitoramento contínuo de disponibilidade, tempo de resposta e
                qualidade de serviço.
              </p>
            </div>

            <div className="col-md-4 text-end">
              <div className="d-grid gap-2">
                <button
                  className="btn btn-primary btn-lg"
                  onClick={() => handlePlatformHealthCheck()}
                  disabled={isLoading}
                >
                  <FiActivity className="me-2" size={18} />
                  Checar Agora
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    pushToastify({
                      message: "Em construção...",
                      type: "info",
                      theme: "dark",
                      pauseOnHover: true,
                    });
                  }}
                >
                  <FaClock className="me-2" size={16} />
                  Histórico
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    pushToastify({
                      message: "Em construção...",
                      type: "info",
                      theme: "dark",
                      pauseOnHover: true,
                    });
                  }}
                >
                  <FaServer className="me-2" size={16} />
                  Configurações
                </button>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Page Content */}
      {isLoading ? (
        <>
          <div className="col-12 mb-3">
            <LoadingSkeleton height={15} />
          </div>
          <div className="col-12 mb-3">
            <LoadingSkeleton height={15} />
          </div>
          <div className="col-12 mb-3">
            <LoadingSkeleton height={15} />
          </div>
        </>
      ) : (
        platforms.map((platform) => {
          const lastHealthStatisInfo = getPlatformLastHealthStatusInfo(
            platform.id,
            platform.lastHealthCheck
          );
          const StatusIcon = lastHealthStatisInfo.icon;
          const platformStatusInfo = getPlatformStatusInfo(platform.status);
          const logType = getPlatformLogTypeInfo(platform.log?.type);
          const pingBadge = getPlatformLogPingInfo(platform.log?.ping);
          const contentLoadedBadge = getPlatformLogContentLoadedInfo(
            platform.log?.contentLoaded
          );

          return (
            <div key={platform.id} className="col-12 mb-3">
              <Card
                className="border-start border-4"
                style={{
                  borderColor: `var(--bs-${lastHealthStatisInfo.badge})`,
                }}
              >
                <div className="card-header">
                  <div className="d-flex align-items-center justify-content-between">
                    <div
                      className="d-flex align-items-center"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        window.open(
                          platform.url,
                          "_blank",
                          "noopener,noreferrer"
                        );
                      }}
                    >
                      <img
                        className="me-3"
                        src={lastHealthStatisInfo.logo}
                        alt="Logo"
                        style={{ height: "3rem" }}
                      />
                      <div>
                        <h5 className="mb-0">{platform.name}</h5>
                        {platform.description && (
                          <small className="text-muted">
                            {platform.description}
                          </small>
                        )}
                      </div>
                    </div>

                    <div className="d-flex align-items-center">
                      <StatusIcon
                        size={24}
                        className={`text-${lastHealthStatisInfo.badge} me-2`}
                      />
                      <span
                        className={`badge bg-${lastHealthStatisInfo.badge} fs-6 px-3 py-2`}
                      >
                        {lastHealthStatisInfo.text}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  {platform.log && (
                    <>
                      <div className="row g-3 mb-3">
                        <div className="col-md-3">
                          <div className="p-3 bg-light rounded">
                            <div className="text-muted small mb-1">
                              <FaServer size={14} className="me-1" /> Status
                              HTTP
                            </div>
                            <span
                              className={`badge ${
                                platform.log.httpStatus === 200
                                  ? "bg-success"
                                  : "bg-danger"
                              } fs-6 px-3 py-1`}
                            >
                              {platform.log.httpStatus}
                            </span>
                          </div>
                        </div>

                        <div className="col-md-3">
                          <div className="p-3 bg-light rounded">
                            <div className="text-muted small mb-1">
                              <FiZap size={14} className="me-1" /> Tempo de
                              Resposta
                            </div>
                            <div className="fw-semibold">
                              {formatResponseTime(platform.log.responseTime)}
                            </div>
                          </div>
                        </div>

                        {pingBadge && (
                          <div className="col-md-3">
                            <div className="p-3 bg-light rounded">
                              <div className="text-muted small mb-1">
                                <FaWifi size={14} className="me-1" /> Ping
                              </div>
                              <span
                                className={`badge bg-${pingBadge.class} fs-6 px-3 py-1`}
                              >
                                {pingBadge.text}
                              </span>
                            </div>
                          </div>
                        )}

                        {contentLoadedBadge && (
                          <div className="col-md-3">
                            <div className="p-3 bg-light rounded">
                              <div className="text-muted small mb-1">
                                <FiFileText size={14} className="me-1" />
                                Conteúdo Carregado
                              </div>
                              <span
                                className={`badge bg-${contentLoadedBadge.class} fs-6 px-3 py-1`}
                              >
                                {contentLoadedBadge.text}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="row g-3">
                        {platform.log.type && (
                          <div className="col-md-4">
                            <div className="p-3 bg-light rounded">
                              <div className="text-muted small mb-1">
                                <FaList size={14} className="me-1" /> Tipo de
                                Log
                              </div>
                              <span
                                className={`badge bg-${logType.class} fs-6 px-3 py-1`}
                              >
                                {logType.text}
                              </span>
                            </div>
                          </div>
                        )}

                        {platform.log.customer && (
                          <div className="col-md-4">
                            <div className="p-3 bg-light rounded">
                              <div className="text-muted small mb-1">
                                <FaUser size={14} className="me-1" /> Usuário
                              </div>
                              <div className="fw-semibold">
                                {platform.log.customerName
                                  ? formatName(platform.log.customerName)
                                  : `ID: ${platform.log.customer}`}
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="col-md-4">
                          <div className="p-3 bg-light rounded">
                            <div className="text-muted small mb-1">
                              <FaClock size={14} className="me-1" /> Última
                              Verificação
                            </div>
                            <div className="fw-semibold">
                              {formatDate(platform.updatedAt)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {platform.log?.message && (
                    <div className="alert alert-warning mt-3 mb-0">
                      <strong>Mensagem:</strong> {platform.log.message}
                    </div>
                  )}
                </div>

                <div className="card-footer">
                  <div className="d-flex justify-content-between">
                    <div className="py-2">
                      <span
                        className="btn btn-outline-secondary me-3"
                        title={platformStatusInfo?.text}
                        style={{ cursor: "default" }}
                      >
                        Monitoramento:
                        <platformStatusInfo.icon
                          size={16}
                          className={`ms-1 text-${platformStatusInfo.color}`}
                        />
                      </span>
                    </div>
                    <div className="py-2">
                      <a
                        onClick={() => {
                          navigate(`/platformHealth/${platform.id}`);
                        }}
                        className="btn btn-sm btn-outline-secondary me-3"
                      >
                        <FaHistory size={16} className="me-1" />
                        Histórico
                      </a>
                      <a
                        href={platform.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-secondary me-3"
                      >
                        <CgGlobe size={16} className="me-1" />
                        Acessar Página
                      </a>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          );
        })
      )}
    </>
  );
}
